import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  AddExpenseAction,
  AddFinancial,
  changeStatusSchoolAction,
  deleteExpenseAction,
  loginFinance,
  saveLogin,
  updateSchoolNote,
  updateSchoolPrice,
  updateSchoolPayDate,
  updateSchoolPaymentType,
} from "../../redux/slices/mainDataSlice";
import toast from "react-hot-toast";
import "./Finance.css";
import { fetchData } from "../../redux/slices/mainDataSlice";
import Can from "../../permissions/Can";

// تحويل الأرقام الهندية/الفارسية إلى غربية — حماية من الفرونت
// لأن البيانات تأتي من الـ API المباشر
const normalizeDigits = (value) => {
  if (value == null) return value;
  return String(value)
    .replace(/[٠-٩]/g, (d) => "٠١٢٣٤٥٦٧٨٩".indexOf(d))
    .replace(/[۰-۹]/g, (d) => "۰۱۲۳۴۵۶۷۸۹".indexOf(d));
};

const toNumber = (value) => Number(normalizeDigits(value) || 0);

export default function Finance() {
  const [filter, setFilter] = useState("paid");
  const [searchQuery, setSearchQuery] = useState("");
  const [activeSchoolId, setActiveSchoolId] = useState(null);
  const [showAddExpenseBox, setShowAddExpenseBox] = useState(false);
  const [addExpenseName, setAddExpenseName] = useState("");
  const [addExpenseAmount, setAddExpenseAmount] = useState("");
  const [receivedAmount, setReceivedAmount] = useState("200");
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedExpenses, setSelectedExpenses] = useState([]);
  const [openPaymentDropdown, setOpenPaymentDropdown] = useState(null);
  const schoolRefs = useRef({});
  const dropdownRefs = useRef({});

  const handleFilterChange = (filter) => {
    setFilter(filter);
    setActiveSchoolId(null); // Reset active school when filter changes
    setSelectedExpenses([]); // Clear selection when switching filters
    setIsEditMode(false); // Reset edit mode when switching filters
  };

  const toggleExpenseSelection = (id) => {
    setSelectedExpenses((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const toggleAllExpenses = () => {
    if (selectedExpenses.length === expensesData?.length) {
      setSelectedExpenses([]);
    } else {
      setSelectedExpenses(expensesData?.map((expense) => expense.id) || []);
    }
  };

  const handleDeleteSelected = () => {
    if (selectedExpenses.length === 0) {
      toast.error("يرجى تحديد المصروفات المراد حذفها أولاً");
      return;
    }

    toast(
      (t) => (
        <div style={{ fontFamily: "Cairo, sans-serif", textAlign: "center" }}>
          <div>هل ترغب فعلاً في حذف {selectedExpenses.length} مصروف؟</div>
          <div
            style={{
              marginTop: 10,
              display: "flex",
              justifyContent: "center",
              gap: "10px",
            }}
          >
            <button
              onClick={async () => {
                // Delete all selected expenses sequentially and then refresh
                try {
                  await Promise.all(
                    selectedExpenses.map((id) =>
                      dispatch(deleteExpenseAction(id)),
                    ),
                  );
                  await dispatch(fetchData(true));
                  toast.success("تم حذف المصروفات المحددة بنجاح");
                  setSelectedExpenses([]);
                } catch (error) {
                  toast.error("حدث خطأ أثناء حذف بعض المصروفات");
                } finally {
                  toast.dismiss(t.id);
                }
              }}
              style={{
                backgroundColor: "#e53935",
                color: "white",
                padding: "6px 14px",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                fontFamily: "Cairo, sans-serif",
              }}
            >
              نعم
            </button>
            <button
              onClick={() => toast.dismiss(t.id)}
              style={{
                backgroundColor: "#ccc",
                color: "black",
                padding: "6px 14px",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                fontFamily: "Cairo, sans-serif",
              }}
            >
              إلغاء
            </button>
          </div>
        </div>
      ),
      { duration: 8000 },
    );
  };

  const handleClosedSchool = (rgmw) => {
    const isOpening = activeSchoolId !== rgmw;
    setActiveSchoolId((prev) => (prev === rgmw ? null : rgmw));
    setIsEditMode(false);
    if (isOpening) {
      const currentSchool = schoolsData?.find((s) => s.rgmw === rgmw);
      if (currentSchool) {
        if (toNumber(currentSchool.price) > 0) {
          setReceivedAmount("");
        } else {
          setReceivedAmount("200");
        }
      }
    }
    setTimeout(() => {
      const target = schoolRefs.current[rgmw];
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 50);
  };

  const handleAddExpenseClick = () => {
    setShowAddExpenseBox(true);
  };

  const handleFormSubmit = () => {
    if (!activeSchoolId) {
      toast.error("يرجى اختيار مدرسة أولاً");
      return;
    }

    const currentSchool = schoolsData?.find((s) => s.rgmw === activeSchoolId);

    // إذا كانت المدرسة مجاني أو دفع مسبق، نستخدم مبلغ 0
    let amount = toNumber(receivedAmount || "200");
    if (currentSchool?.payment_type === "donation" || currentSchool?.payment_type === "prepaid" || currentSchool?.payment_type === "pre" || currentSchool?.payment_type === "") {
      amount = 0;
    } else if (isNaN(amount) || amount <= 0) {
      toast.error("يرجى إدخال مبلغ صحيح");
      return;
    }

    const data = {
      rgmw: activeSchoolId,
      price: amount,
      pay_date: new Date().toISOString().slice(0, 19).replace("T", " "),
    };

    if (isEditMode) {
      dispatch(updateSchoolPrice(data))
        .unwrap()
        .then(() => {
          toast.success("تم تعديل المبلغ بنجاح");
          setIsEditMode(false);
          setReceivedAmount("");
          // Refresh data after successful update
          dispatch(fetchData(true));
        })
        .catch((error) => {
          toast.error("حدث خطأ أثناء تعديل المبلغ");
        });
    } else {
      // Use the async thunk directly instead of the action creator
      dispatch(AddFinancial(data))
        .unwrap()
        .then(() => {
          toast.success("تم إضافة المبلغ بنجاح");
          setReceivedAmount("");
          // Refresh data after successful addition
          dispatch(fetchData(true));
        })
        .catch((error) => {
          toast.error("حدث خطأ أثناء إضافة المبلغ");
        });
    }
  };

  const handleEditClick = () => {
    setIsEditMode(true);
    const currentSchool = schoolsData?.find((s) => s.rgmw === activeSchoolId);
    if (currentSchool) {
      setReceivedAmount("");
    }
  };

  const handleFormExpenseSubmit = async () => {
    if (!addExpenseName.trim() || !addExpenseAmount) {
      toast.error("يرجى ملء جميع الحقول");
      return;
    }

    const data = {
      name: addExpenseName,
      price: addExpenseAmount,
    };

    try {
      const loadingToast = toast.loading("جاري إضافة المصروف...");
      await dispatch(AddExpenseAction(data));
      await dispatch(fetchData(true));
      toast.dismiss(loadingToast);
      toast.success("تم إضافة المصروف بنجاح");

      // Reset and close
      setAddExpenseName("");
      setAddExpenseAmount("");
      setShowAddExpenseBox(false);
    } catch (error) {
      toast.error("فشل في إضافة المصروف");
    }
  };
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, []);

  // Close custom dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (openPaymentDropdown !== null) {
        const ref = dropdownRefs.current[openPaymentDropdown];
        if (ref && !ref.contains(e.target)) {
          setOpenPaymentDropdown(null);
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openPaymentDropdown]);

  const isAuth = useSelector((state) => state.main.isAuthenticated);
  const schoolsData = useSelector((state) => state.main.data?.schoolData);
  const expensesData = useSelector((state) => state.main.data?.expensesData);
  const financialData = useSelector((state) => state.main.data?.financialData);
  const schoolYear = useSelector(
    (state) => state.main.data?.alfaresVersionData?.[0]?.school_year,
  );
  const isFinanceLogged = useSelector((state) => state.main.isFinanceLogged);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const getPaymentBadgeInfo = (school) => {
    const totalReceived =
      toNumber(school.price) +
      (financialData
        ?.filter((sch) => sch.rgmw === school.rgmw)
        ?.reduce(
          (total, financial) => total + toNumber(financial.price),
          0,
        ) || 0);

    const pt = school.payment_type;

    if (pt === "donation") {
      return { text: "مجاني", className: "badge-donation" };
    }

    if (pt === "prepaid" || pt === "pre" || pt === "") {
      return { text: "دفع مسبق", className: "badge-prepaid" };
    }

    if (totalReceived === 0) {
      if (filter === "unpaid")
        return { text: "مؤقتة", className: "badge-unpaid" };
      if (filter === "closed")
        return { text: "مغلق", className: "badge-notpaid" };
      return { text: "غير مسدد", className: "badge-notpaid" };
    }

    if (pt === "discounted") {
      return { text: "مخفض", className: "badge-discounted" };
    }

    return { text: "مدفوع", className: "badge-full" };
  };

  const handlePaymentTypeChange = async (rgmw, paymentType) => {
    try {
      await dispatch(
        updateSchoolPaymentType({ rgmw, payment_type: paymentType }),
      ).unwrap();
      await dispatch(fetchData(true));
    } catch (error) {
      toast.error("فشل في تحديث نوع الدفع");
    }
  };
  useEffect(() => {
    if (!isAuth) {
      const storedUserCredentials = JSON.parse(
        localStorage.getItem("userCredentials"),
      );
      if (storedUserCredentials) {
        dispatch(saveLogin(storedUserCredentials));
      } else {
        navigate("/");
      }
    }
  }, [isAuth, dispatch, navigate]);

  const changeSchoolStatusHandler = async (rgmw) => {
    try {
      await dispatch(changeStatusSchoolAction(rgmw));
      toast.success("تم التسديد بنجاح");
      // تحديث رسالة المدرسة بحقل noteforschool (جدول schoolstbl) بتجديد الاشتراك السنوي — بصمت
      try {
        await dispatch(
          updateSchoolNote({
            rgmw,
            noteforschool: `تم تجديد الاشتراك السنوي لمنصة الفارس${schoolYear ? ` للعام الدراسي ${schoolYear}` : ""}`,
          }),
        ).unwrap();
      } catch (noteError) {
        console.error("updateSchoolNote failed:", noteError);
      }
      await dispatch(fetchData(true));
    } catch (error) {
      toast.error("فشل في عملية التسديد");
    }
  };

  const getSchoolTotalPaid = useCallback(
    (school) => {
      return (
        toNumber(school?.price) +
        (financialData
          ?.filter((sch) => sch.rgmw === school?.rgmw)
          ?.reduce(
            (total, financial) => total + toNumber(financial.price),
            0,
          ) || 0)
      );
    },
    [financialData],
  );

  const filteredSchools = useMemo(() => {
    return (
      schoolsData?.filter((school) => {
        const matchesFilter =
          filter === "paid"
            ? school.onoff === "on" && school.paydate === null
            : filter === "unpaid"
              ? school.onoff === "on" &&
                school.paydate !== null &&
                new Date(school.paydate) >= new Date() &&
                getSchoolTotalPaid(school) === 0
              : filter === "closed"
                ? school.onoff === "off" ||
                  (school.onoff === "on" &&
                   school.paydate !== null &&
                   new Date(school.paydate) < new Date())
                : filter === "paid_schools"
                  ? school.onoff === "on" && getSchoolTotalPaid(school) > 0
                  : true;

        const matchesSearch = searchQuery
          ? (school.schoolname || "").toLowerCase().includes(searchQuery)
          : true;

        return matchesFilter && matchesSearch;
      }) || []
    );
  }, [schoolsData, filter, searchQuery, getSchoolTotalPaid]);
  // paid => on - paydate == null
  // unpaid => on - paydate >= nowDate
  // closed => on - paydate < nowDate

  const [pins, setPins] = useState(["", "", "", ""]);
  const inputRefs = useRef([]);

  useEffect(() => {
    // Hide scrollbar when PIN page is active
    if (!isFinanceLogged) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    // Cleanup function to restore scrollbar when component unmounts
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isFinanceLogged]);

  useEffect(() => {
    if (pins[3] === "" && inputRefs.current[3]) {
      inputRefs.current[3].focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (index, value) => {
    const newPins = [...pins];
    newPins[index] = value;
    setPins(newPins);

    const pinCode = newPins.join("");
    if (pinCode.length === 4) {
      handleLogin(pinCode);
    } else {
      // الانتقال للمربع التالي (باتجاه اليمين)
      if (value.length === 1 && index > 0) {
        inputRefs.current[index - 1].focus();
      }
    }
  };

  const handleLogin = (pinCode) => {
    setTimeout(() => {
      // عكس الترتيب للتحقق لأننا نكتب من اليسار لليمين (3، 2، 1، 0)
      const reversedPinCode = pinCode.split("").reverse().join("");
      if (reversedPinCode === "1232") {
        dispatch(loginFinance());
      } else {
        toast.error("كلمة مرور خاطئة");
        setPins(["", "", "", ""]);
        if (inputRefs.current[3]) {
          inputRefs.current[3].focus();
        }
      }
    }, 300);
  };

  const handleKeyDown = (index, event) => {
    // العودة للمربع السابق على اليسار عند الضغط على Backspace
    if (event.key === "Backspace" && !pins[index] && index < 3) {
      inputRefs.current[index + 1].focus();
    }
  };

  const totalIncome = useMemo(() => {
    // المبلغ الإجمالي = مجموع كل ما دفعته المدارس النشطة (بنفس منطق getSchoolTotalPaid)
    return (schoolsData || [])
      .filter((school) => school.onoff === "on")
      .reduce((total, school) => total + getSchoolTotalPaid(school), 0);
  }, [schoolsData, financialData, getSchoolTotalPaid]);

  const totalExpenses = useMemo(() => {
    return (
      expensesData?.reduce(
        (total, expense) => total + toNumber(expense.price),
        0,
      ) || 0
    );
  }, [expensesData]);

  const netBalance = totalIncome - totalExpenses;

  const handleRemoveClosingDate = (rgmw) => {
    toast(
      (t) => (
        <div style={{ fontFamily: "Cairo, sans-serif", textAlign: "center" }}>
          <div>هل ترغب فعلاً في إزالة تاريخ الإغلاق وإعادة المدرسة؟</div>
          <div
            style={{
              marginTop: 10,
              display: "flex",
              justifyContent: "center",
              gap: "10px",
            }}
          >
            <button
              onClick={() => {
                dispatch(updateSchoolPayDate({ rgmw, date: null }));
                toast.success("تم إزالة تاريخ الإغلاق بنجاح");
                toast.dismiss(t.id);
              }}
              style={{
                backgroundColor: "#e53935",
                color: "white",
                padding: "6px 14px",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                fontFamily: "Cairo, sans-serif",
              }}
            >
              نعم
            </button>
            <button
              onClick={() => toast.dismiss(t.id)}
              style={{
                backgroundColor: "#ccc",
                color: "black",
                padding: "6px 14px",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                fontFamily: "Cairo, sans-serif",
              }}
            >
              إلغاء
            </button>
          </div>
        </div>
      ),
      { duration: 8000 },
    );
  };

  return isFinanceLogged ? (
    <div className="finance-page anim-fade-in">
      <div className="finance-container premium-edit-container">
        <div className="finance-content" data-aos="fade-in">
          <div className="balance-card-grid" data-aos="zoom-in">
            <div className="stat-card sum-card">
              <div className="stat-icon">
                <i className="fa-solid fa-money-bill-wave"></i>
              </div>
              <div className="stat-info">
                <h3>المبلغ الاجمالي</h3>
                <span className="stat-value">{totalIncome}</span>
              </div>
            </div>

            <div className="stat-card expense-card">
              <div className="stat-icon">
                <i className="fa-solid fa-arrow-trend-down"></i>
              </div>
              <div className="stat-info">
                <h3>مجموع المصروفات</h3>
                <span className="stat-value">{totalExpenses}</span>
              </div>
            </div>

            <div className="stat-card balance-card">
              <div className="stat-icon">
                <i className="fa-solid fa-vault"></i>
              </div>
              <div className="stat-info">
                <h3>الرصيد المتبقي</h3>
                <span className="stat-value">{netBalance}</span>
              </div>
            </div>
          </div>

          <div className="filter-tabs">
            <button
              className={`filter-tab ${filter === "paid" ? "active" : ""}`}
              onClick={() => handleFilterChange("paid")}
            >
              النشطة
            </button>
            <button
              className={`filter-tab ${filter === "paid_schools" ? "active" : ""}`}
              onClick={() => handleFilterChange("paid_schools")}
            >
              المسدد
            </button>
            <button
              className={`filter-tab ${filter === "unpaid" ? "active" : ""}`}
              onClick={() => handleFilterChange("unpaid")}
            >
              غير مسدد
            </button>
            <button
              className={`filter-tab ${filter === "closed" ? "active" : ""}`}
              onClick={() => handleFilterChange("closed")}
            >
              مغلق
            </button>
            <button
              className={`filter-tab ${filter === "expenses" ? "active" : ""}`}
              onClick={() => handleFilterChange("expenses")}
            >
              المصروفات
            </button>
          </div>
          {filter !== "expenses" && (
            <div className="search-container large-input">
              <i className="fa-solid fa-magnifying-glass"></i>
              <input
                type="text"
                placeholder="ابحث في اسم المدرسة"
                onChange={(e) => setSearchQuery(e.target.value.toLowerCase())}
                onFocus={() => setActiveSchoolId(null)}
              />
              <span className="search-results-count">
                {filteredSchools?.length || 0}
              </span>
            </div>
          )}

          {filter === "expenses" ? (
            <div className="expenses-container">
              <div className="expenses-header">
                <div className="delete-selected-container">
                  {selectedExpenses.length > 0 && (
                    <Can module="finance" action="delete">
                    <button
                      className="delete-selected-btn anim-pop-in"
                      onClick={handleDeleteSelected}
                    >
                      <i className="fa-solid fa-trash-can"></i>
                      <span>حذف المحدد ({selectedExpenses.length})</span>
                    </button>
                    </Can>
                  )}
                </div>
                <Can module="finance" action="add">
                <button
                  className="add-expense-btn"
                  onClick={handleAddExpenseClick}
                >
                  <i className="fa-solid fa-plus"></i>
                  إضافة مصروف
                </button>
                </Can>
              </div>
              <div className="expenses-table-wrapper">
                <table className="expenses-table">
                  <thead>
                    <tr>
                      <th className="checkbox-col">
                        <input
                          type="checkbox"
                          checked={
                            selectedExpenses.length > 0 &&
                            selectedExpenses.length === expensesData?.length
                          }
                          onChange={toggleAllExpenses}
                        />
                      </th>
                      <th>اسم المصروف</th>
                      <th>المبلغ</th>
                      <th>التاريخ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {expensesData?.map((expense) => (
                      <tr
                        key={expense.id}
                        className={
                          selectedExpenses.includes(expense.id)
                            ? "selected-row"
                            : ""
                        }
                      >
                        <td className="checkbox-col">
                          <input
                            type="checkbox"
                            checked={selectedExpenses.includes(expense.id)}
                            onChange={() => toggleExpenseSelection(expense.id)}
                          />
                        </td>
                        <td
                          data-label="البيان:"
                          onClick={() => toggleExpenseSelection(expense.id)}
                          style={{ cursor: "pointer" }}
                        >
                          {expense.name}
                        </td>
                        <td data-label="السعر:">{normalizeDigits(expense.price)}</td>
                        <td data-label="التاريخ:">
                          {new Date(expense.date).toLocaleDateString("en-GB")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="schools-section">
              {filteredSchools?.map((school) => (
                <div
                  key={school.rgmw}
                  ref={(el) => (schoolRefs.current[school.rgmw] = el)}
                  className={`school-card ${
                    activeSchoolId === school.rgmw ? "active" : ""
                  }`}
                >
                  <div
                    className={`school-header ${
                      school.rgmw === activeSchoolId ? "active" : ""
                    }`}
                    onClick={() => handleClosedSchool(school.rgmw)}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="school-info">
                      {filter === "closed" ? (
                        <button
                          className="delete-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveClosingDate(school.rgmw);
                          }}
                        >
                          <i className="fa-solid fa-trash-can"></i>
                        </button>
                      ) : null}
                      <div className="school-name-container">
                        <h3>{school.schoolname}</h3>
                        {filter === "unpaid" && school.paydate && (
                          <div
                            className="school-extension-date"
                            style={{
                              color:
                                school.rgmw === activeSchoolId
                                  ? "var(--text)"
                                  : "#ef4444",
                              fontSize: "12px",
                              marginTop: "4px",
                            }}
                          >
                            تاريخ التمديد:{" "}
                            {new Date(school.paydate).toLocaleDateString(
                              "en-GB",
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div
                      className="school-amount"
                      style={{
                        color:
                          activeSchoolId === school.rgmw
                            ? "var(--text)"
                            : "var(--text)",
                      }}
                    >
                      {filter === "paid" || filter === "paid_schools"
                        ? null
                        : normalizeDigits(school.price)}
                      <span
                        className={`payment-badge ${getPaymentBadgeInfo(school).className}`}
                      >
                        {getPaymentBadgeInfo(school).text}
                      </span>
                    </div>
                  </div>

                  {school.rgmw === activeSchoolId && (
                    <div className="school-details" data-aos="zoom-in-down">
                      {(filter === "paid" || filter === "paid_schools") && (
                        <>
                          <div
                            style={{
                              color:
                                getSchoolTotalPaid(school) > 0
                                  ? "#10b981"
                                  : "#ef4444",
                              fontSize: "1.2em",
                              fontWeight: 400,
                              marginBottom: "5px",
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              justifyContent: "space-between",
                            }}
                          >
                            {(() => {
                              const finRow = financialData?.find(
                                (f) => f.rgmw === school.rgmw,
                              );
                              const payDateVal =
                                finRow?.pay_date || school.pay_date_fin;
                              let dateText = "";
                              if (payDateVal) {
                                const d = new Date(payDateVal);
                                if (!isNaN(d.getTime())) {
                                  try {
                                    dateText = d.toLocaleString("en-GB", {
                                      timeZone: "Asia/Jerusalem",
                                      year: "numeric",
                                      month: "2-digit",
                                      day: "2-digit",
                                      hour: "2-digit",
                                      minute: "2-digit",
                                      hour12: true,
                                    });
                                  } catch {
                                    dateText = d.toLocaleString("en-GB", {
                                      year: "numeric",
                                      month: "2-digit",
                                      day: "2-digit",
                                      hour: "2-digit",
                                      minute: "2-digit",
                                      hour12: true,
                                    });
                                  }
                                } else {
                                  dateText = String(payDateVal).replace(
                                    "T",
                                    " ",
                                  );
                                }
                              }
                              return (
                                <>
                                  {dateText && (
                                    <div
                                      style={{
                                        fontSize: "12px",
                                        fontWeight: "600",
                                        color: "#6b7280",
                                        fontFamily: "Cairo, sans-serif",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "6px",
                                        direction: "rtl",
                                      }}
                                    >
                                      <i
                                        className="fa-solid fa-clock"
                                        style={{ fontSize: "11px" }}
                                      ></i>
                                      <span>{dateText}</span>
                                    </div>
                                  )}
                                  {getSchoolTotalPaid(school) > 0 && (
                                    <Can module="finance" action="delete">
                                    <i
                                      className="fa-solid fa-trash"
                                      style={{
                                        color: "#ef4444",
                                        cursor: "pointer",
                                        fontSize: "1em",
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toast(
                                          (t) => (
                                            <div
                                              style={{
                                                fontFamily: "Cairo, sans-serif",
                                                textAlign: "center",
                                              }}
                                            >
                                              <div>
                                                هل ترغب فعلاً في حذف هذا المبلغ؟
                                              </div>
                                              <div
                                                style={{
                                                  marginTop: 10,
                                                  display: "flex",
                                                  justifyContent: "center",
                                                  gap: "10px",
                                                }}
                                              >
                                                <button
                                                  onClick={async () => {
                                                    try {
                                                      const credentials =
                                                        JSON.parse(
                                                          localStorage.getItem(
                                                            "userCredentials",
                                                          ),
                                                        );
                                                      const token =
                                                        credentials?.token;

                                                      const res = await fetch(
                                                        `https://api.alfares.cloud/api/alfares/deleteSchoolPrice/${school.rgmw}/${token}`,
                                                        { method: "DELETE" },
                                                      );

                                                      if (!res.ok)
                                                        throw new Error(
                                                          "فشل في حذف المبلغ",
                                                        );

                                                      toast.success(
                                                        "تم حذف المبلغ بنجاح",
                                                      );
                                                      dispatch(fetchData());
                                                    } catch {
                                                      toast.error(
                                                        "حدث خطأ أثناء الحذف",
                                                      );
                                                    } finally {
                                                      toast.dismiss(t.id);
                                                    }
                                                  }}
                                                  style={{
                                                    backgroundColor: "#e53935",
                                                    color: "white",
                                                    padding: "6px 14px",
                                                    border: "none",
                                                    borderRadius: "6px",
                                                    cursor: "pointer",
                                                    fontFamily:
                                                      "Cairo, sans-serif",
                                                  }}
                                                >
                                                  نعم
                                                </button>
                                                <button
                                                  onClick={() =>
                                                    toast.dismiss(t.id)
                                                  }
                                                  style={{
                                                    backgroundColor: "#ccc",
                                                    color: "black",
                                                    padding: "6px 14px",
                                                    border: "none",
                                                    borderRadius: "6px",
                                                    cursor: "pointer",
                                                    fontFamily:
                                                      "Cairo, sans-serif",
                                                  }}
                                                >
                                                  إلغاء
                                                </button>
                                              </div>
                                            </div>
                                          ),
                                          {
                                            duration: 8000,
                                            id: "delete-toast",
                                          },
                                        );
                                      }}
                                    ></i>
                                    </Can>
                                  )}
                                </>
                              );
                            })()}
                          </div>

                          {/* ── Custom Payment-Type Dropdown ── */}
                          <div
                            className="payment-type-selector"
                            ref={(el) =>
                              (dropdownRefs.current[school.rgmw] = el)
                            }
                          >
                            <label className="payment-type-label">
                              <i
                                className="fa-solid fa-credit-card"
                                style={{ marginLeft: "5px", fontSize: "11px" }}
                              ></i>
                              نوع الدفع
                            </label>
                            <div className="custom-dropdown-wrapper">
                              <button
                                type="button"
                                className={`custom-dropdown-trigger ${
                                  openPaymentDropdown === school.rgmw
                                    ? "is-open"
                                    : ""
                                } pt-${school.payment_type || "full"}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenPaymentDropdown(
                                    openPaymentDropdown === school.rgmw
                                      ? null
                                      : school.rgmw,
                                  );
                                }}
                              >
                                <span className="custom-dropdown-selected">
                                  {school.payment_type === "discounted" && (
                                    <>
                                      <i className="fa-solid fa-percent"></i>{" "}
                                      مخفض
                                    </>
                                  )}
                                  {school.payment_type === "donation" && (
                                    <>
                                      <i className="fa-solid fa-hand-holding-heart"></i>{" "}
                                      مجاني
                                    </>
                                  )}
                                  {(school.payment_type === "prepaid" || school.payment_type === "pre" || school.payment_type === "") && (
                                    <>
                                      <i className="fa-solid fa-clock"></i>{" "}
                                      دفع مسبق
                                    </>
                                  )}
                                  {(!school.payment_type ||
                                    school.payment_type === "full") && (
                                    <>
                                      <i className="fa-solid fa-circle-check"></i>{" "}
                                      مدفوع
                                    </>
                                  )}
                                </span>
                                <i
                                  className={`fa-solid fa-chevron-down custom-dropdown-arrow ${
                                    openPaymentDropdown === school.rgmw
                                      ? "rotated"
                                      : ""
                                  }`}
                                ></i>
                              </button>

                              {openPaymentDropdown === school.rgmw && (
                                <ul
                                  className="custom-dropdown-menu"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {[
                                    {
                                      value: "full",
                                      icon: "fa-circle-check",
                                      label: "مدفوع",
                                      cls: "opt-full",
                                    },
                                    {
                                      value: "discounted",
                                      icon: "fa-percent",
                                      label: "مخفض",
                                      cls: "opt-disc",
                                    },
                                    {
                                      value: "donation",
                                      icon: "fa-hand-holding-heart",
                                      label: "مجاني",
                                      cls: "opt-dona",
                                    },
                                    {
                                      value: "prepaid",
                                      icon: "fa-clock",
                                      label: "دفع مسبق",
                                      cls: "opt-prep",
                                    },
                                  ].map((opt) => (
                                    <li
                                      key={opt.value}
                                      className={`custom-dropdown-item ${opt.cls} ${
                                        (school.payment_type || "full") === opt.value ||
                                        (opt.value === "prepaid" && (school.payment_type === "pre" || school.payment_type === ""))
                                          ? "selected"
                                          : ""
                                      }`}
                                      onClick={async () => {
                                        setOpenPaymentDropdown(null);
                                        await handlePaymentTypeChange(
                                          school.rgmw,
                                          opt.value,
                                        );
                                      }}
                                    >
                                      <i className={`fa-solid ${opt.icon}`}></i>
                                      {opt.label}
                                      {((school.payment_type || "full") === opt.value ||
                                        (opt.value === "prepaid" && (school.payment_type === "pre" || school.payment_type === ""))) && (
                                        <i className="fa-solid fa-check opt-check"></i>
                                      )}
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          </div>

                          {filter === "paid_schools" ? (
                            <>
                              {isEditMode ? (
                                <input
                                  type="number"
                                  placeholder="أدخل مبلغ التعديل"
                                  value={receivedAmount}
                                  onChange={(e) =>
                                    setReceivedAmount(
                                      normalizeDigits(e.target.value),
                                    )
                                  }
                                  style={{
                                    width: "100%",
                                    padding: "8px",
                                    marginBottom: "10px",
                                    borderRadius: "6px",
                                    border: "1px solid var(--border)",
                                    fontFamily: "Cairo, sans-serif",
                                    backgroundColor: "var(--panel-2)",
                                    color: "var(--text)",
                                  }}
                                />
                              ) : null}

                              <div
                                style={{
                                  display: "flex",
                                  gap: "10px",
                                  marginTop: "10px",
                                }}
                              >
                                {isEditMode && (
                                  <Can module="finance" action="edit">
                                  <button
                                    className="add-amount-btn"
                                    onClick={handleFormSubmit}
                                    style={{ flex: 1 }}
                                  >
                                    <i className="fa-solid fa-check"></i>
                                    <span>تطبيق التعديل</span>
                                  </button>
                                  </Can>
                                )}

                                {getSchoolTotalPaid(school) > 0 &&
                                  !isEditMode && (
                                    <Can module="finance" action="edit">
                                    <button
                                      className="add-amount-btn"
                                      onClick={handleEditClick}
                                      style={{
                                        flex: 1,
                                        backgroundColor: "#28917f",
                                      }}
                                    >
                                      <i className="fa-solid fa-edit"></i>
                                      <span>تعديل</span>
                                    </button>
                                    </Can>
                                  )}
                              </div>
                            </>
                          ) : (
                            <>
                              <input
                                type="number"
                                placeholder={
                                  toNumber(school.price) > 0
                                    ? "أدخل مبلغ التعديل"
                                    : "أدخل المبلغ"
                                }
                                value={receivedAmount}
                                onChange={(e) =>
                                  setReceivedAmount(
                                    normalizeDigits(e.target.value),
                                  )
                                }
                                disabled={
                                  toNumber(school.price) > 0 && !isEditMode
                                }
                                style={{
                                  width: "100%",
                                  padding: "8px",
                                  marginBottom: "10px",
                                  borderRadius: "6px",
                                  border: "1px solid var(--border)",
                                  fontFamily: "Cairo, sans-serif",
                                  backgroundColor:
                                    toNumber(school.price) > 0 && !isEditMode
                                      ? "var(--bg)"
                                      : "var(--panel-2)",
                                  color: "var(--text)",
                                  cursor:
                                    toNumber(school.price) > 0 && !isEditMode
                                      ? "not-allowed"
                                      : "text",
                                }}
                              />

                              <div
                                style={{
                                  display: "flex",
                                  gap: "10px",
                                  marginTop: "10px",
                                }}
                              >
                                <Can module="finance" action="edit">
                                <button
                                  className="add-amount-btn"
                                  onClick={handleFormSubmit}
                                  style={{ flex: 1 }}
                                >
                                  <i className="fa-solid fa-check"></i>
                                  <span>
                                    {getSchoolTotalPaid(school) > 0
                                      ? "تطبيق التعديل"
                                      : "اضغط للتسديد"}
                                  </span>
                                </button>
                                </Can>

                                {getSchoolTotalPaid(school) > 0 && (
                                  <Can module="finance" action="edit">
                                  <button
                                    className="add-amount-btn"
                                    onClick={handleEditClick}
                                    style={{
                                      flex: 1,
                                      backgroundColor: "#28917f",
                                    }}
                                  >
                                    <i className="fa-solid fa-edit"></i>
                                    <span>تعديل</span>
                                  </button>
                                  </Can>
                                )}
                              </div>
                            </>
                          )}
                        </>
                      )}
                      {filter === "unpaid" && (
                        <button
                          className="mark-paid-btn"
                          onClick={() =>
                            changeSchoolStatusHandler(activeSchoolId)
                          }
                        >
                          <i className="fa-solid fa-check"></i>
                          <span>اضغط للتسديد</span>
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Modern Add Expense Modal */}
        {showAddExpenseBox && (
          <div
            className="premium-modal-overlay"
            onClick={() => setShowAddExpenseBox(false)}
          >
            <div
              className="premium-modal-content anim-pop-in"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <div className="modal-icon">
                  <i className="fa-solid fa-cart-plus"></i>
                </div>
                <h3>إضافة مصروف جديد</h3>
                <button
                  className="close-modal"
                  onClick={() => setShowAddExpenseBox(false)}
                >
                  <i className="fa-solid fa-xmark"></i>
                </button>
              </div>

              <div className="modal-body">
                <div className="premium-input-group">
                  <label>بيان المصروف</label>
                  <div className="input-with-icon">
                    <i className="fa-solid fa-pen-to-square"></i>
                    <input
                      type="text"
                      placeholder="مثال: فاتورة كهرباء، إيجار..."
                      value={addExpenseName}
                      onChange={(e) => setAddExpenseName(e.target.value)}
                      autoFocus
                    />
                  </div>
                </div>

                <div className="premium-input-group">
                  <label>المبلغ (شيكل)</label>
                  <div className="input-with-icon">
                    <i className="fa-solid fa-money-bill-1-wave"></i>
                    <input
                      type="number"
                      placeholder="0.00"
                      value={addExpenseAmount}
                      onChange={(e) => setAddExpenseAmount(e.target.value)}
                      onKeyPress={(e) =>
                        e.key === "Enter" && handleFormExpenseSubmit()
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  className="premium-btn secondary"
                  onClick={() => setShowAddExpenseBox(false)}
                >
                  إلغاء
                </button>
                <Can module="finance" action="add">
                <button
                  className="premium-btn primary"
                  onClick={handleFormExpenseSubmit}
                >
                  إضافة المصروف
                </button>
                </Can>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  ) : (
    <div
      className="pin-code-page"
      style={{
        width: "100%",
        height: "calc(100vh - 100px)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        background: "transparent",
        position: "relative",
        overflow: "hidden", // Prevent scrollbar
      }}
    >
      <div
        className="pin-container"
        data-aos="fade-in"
        style={{
          position: "relative",
          zIndex: 100,
          boxShadow: "0 20px 50px -10px rgba(0,0,0,0.1)",
        }}
      >
        {/* 🔐 أيقونة القفل مع النص */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            marginBottom: "0px",
            color: "var(--text)",
            fontFamily: "Cairo, sans-serif",
            fontSize: "14px",
          }}
        >
          <i
            className="fa-solid fa-lock"
            style={{ fontSize: "25px", opacity: 0.8 }}
          ></i>
          <span className="pin-label">كلمة مرور المالية</span>
        </div>

        {/* 🟦 مربعات الإدخال */}
        <div className="pin-inputs">
          {pins.map((pin, index) => (
            <input
              key={index}
              ref={(el) => (inputRefs.current[index] = el)}
              type="number"
              maxLength={1}
              value={pin}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
